import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  UntypedFormControl,
  ValidationErrors,
} from '@angular/forms';
import {
  countryLabel,
  formatAddress,
} from '@app/components/pr-form/address-query.utils';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import { GoogleMapsLoaderService } from '@app/services/google-maps-loader.service';
import * as _ from 'lodash';
import { of } from 'rxjs';

export interface PlaceSearchResult {
  address: string;
  location?: google.maps.LatLng;
  imageUrl?: string;
  iconUrl?: string;
  name?: string;
}

@Component({
    selector: 'pr-input-maps',
    templateUrl: './pr-input-maps.component.html',
    styleUrls: ['./pr-input-maps.component.scss'],
    standalone: false
})
export class PrInputMapsComponent {
  @ViewChild('mapsInput')
  mapsInput!: ElementRef;

  @Input() placeholder = 'Enter address...';

  @Output() placeChanged = new EventEmitter<PlaceSearchResult>();

  autocomplete: google.maps.places.Autocomplete | undefined;

  listener: any;

  @Input() formGroupName: string;
  @Input() derrors: boolean = false;
  @Input() label: string;
  @Input() isDefault: boolean = true;
  private _isGMaps: boolean = true;
  public get isGMaps(): boolean {
    return this._isGMaps;
  }
  @Input() public set isGMaps(value: boolean) {
    this._isGMaps = value;
    if (this._isGMaps === true && this.mapOptions) this.findManualAdress();
  }
  @Input() disabled: boolean = false;
  @Input() hasCedex: boolean = false;
  @Input() countries = [];
  @Input() documentMapId = 'map';
  @Input() highlightOnValue: boolean = false;
  @Input() highlightOnFocus: boolean = false;

  formattedAddress: string;
  fullAddress: UntypedFormControl;
  markers: [{}] = [{}];
  autocompleteLstnr = null;
  autocompleteService = null;
  mapOptions: google.maps.MapOptions = null;
  marker: {} = {};

  constructor(
    private ngZone: NgZone,
    private controlContainer: ControlContainer,
    private referenceDao: ReferenceDao,
    private googleMapsLoader: GoogleMapsLoaderService,
  ) {}

  ngOnInit() {
    this.loadGMAutoPlaces();
    this.fullAddress = new UntypedFormControl(
      '',
      this.getAddressValidation.bind(this),
    );

    // Écouter les changements du champ autocomplete pour vider les champs sous-jacents
    this.fullAddress.valueChanges.subscribe(value => {
      if (!value || value.trim().length === 0) {
        // Si le champ autocomplete est vidé, vider les champs sous-jacents
        if (this.control) {
          this.control.patchValue({
            street: null,
            zipCode: null,
            city: null,
            country: null,
          });
        }
      }
    });

    let countriesSub = this.referenceDao.getCountries();
    if (this.countries?.length) countriesSub = of(this.countries);

    countriesSub.subscribe(countries => (this.countries = countries));
  }

  loadGMAutoPlaces() {
    // Wait for Google Maps API to be loaded
    this.googleMapsLoader.loadScript().subscribe({
      next: () => {
        setTimeout(() => {
          this.initMap();
          this.initAutocomplete();
          this.createManualEntry();
          this.findManualAdress();

          // Google is removing autocomplete for autocomplete="off" which doesn't work
          setTimeout(() => {
            Array.from(document.querySelectorAll('input[matInput]')).forEach(
              element => {
                element.setAttribute('autocomplete', 'new-address');
              },
            );
          }, 1000);
        }, 0);
      },
      error: err => {
        console.error('Failed to load Google Maps in pr-input-maps', err);
      },
    });
  }

  initAutocomplete() {
    this.autocomplete = new google.maps.places.Autocomplete(
      this.mapsInput.nativeElement,
      {
        types: ['address'],
      },
    );

    this.autocompleteLstnr = this.autocomplete.addListener(
      'place_changed',
      () => {
        this.ngZone.run(() => {
          let place: google.maps.places.PlaceResult =
            this.autocomplete.getPlace();
          if (place.geometry === undefined || place.geometry === null) {
            return;
          }

          this.marker = {
            position: {
              lng: place.geometry.location.lng(),
              lat: place.geometry.location.lat(),
            },
          };
          this.mapOptions = {
            center: {
              lng: place.geometry.location.lng(),
              lat: place.geometry.location.lat(),
            },
            zoom: 16,
            disableDefaultUI: true,
            gestureHandling: 'none',
          };

          //Remove previous markers
          // this.markers.push(marker);
          // _(this.markers)
          //   .reverse()
          //   .map((marker: google.maps.Marker, i) => {
          //     if (i > 0) {
          //       marker.setMap(null);
          //     }
          //   });

          let newAddress: any = this.formatNewAddress(place);
          if (this.hasCedex) newAddress.cedex = null;

          this.control.setValue(newAddress);
          this.fullAddress.setValue(this.getFormatedAddress(place));

          this.fullAddress.updateValueAndValidity();
        });
      },
    );
  }

  getFormatedAddress(place: any) {
    place = this.mappedPlace(place);
    let adr = [
      place.street_number || '',
      place.street + ',' || '',
      place.city + ',' || '',
      place.zipCode + ',' || '0',
      place.country.long_name || '',
    ].join(' ');

    if (adr.trim() == ', ,') adr = null;
    return adr;
  }

  mappedPlace(place): {
    country: { long_name: string; short_name: string };
    city: string;
    zipCode: string;
    street: string;
    street_number: string;
    administrative_area_level_1: string;
    administrative_area_level_2: string;
  } {
    return {
      country: _(place.address_components).find(c =>
        c.types.includes('country'),
      ),
      city: _(place.address_components).find(c => c.types.includes('locality'))
        ?.long_name
        ? _(place.address_components).find(c => c.types.includes('locality'))
            ?.long_name
        : _(place.address_components).find(c =>
            c.types.includes('administrative_area_level_1'),
          )?.long_name,
      zipCode: _(place.address_components).find(c =>
        c.types.includes('postal_code'),
      )?.long_name,
      street: _(place.address_components).find(c => c.types.includes('route'))
        ?.long_name,
      street_number: _(place.address_components).find(c =>
        c.types.includes('street_number'),
      )?.long_name,
      administrative_area_level_1: _(place.address_components).find(c =>
        c.types.includes('administrative_area_level_1'),
      )?.long_name,
      administrative_area_level_2: _(place.address_components).find(c =>
        c.types.includes('administrative_area_level_2'),
      )?.long_name,
    };
  }

  formatNewAddress(place) {
    place = this.mappedPlace(place);

    return {
      street: `${place.street_number ? place.street_number + ' ' : ''}${
        place.street
      }`,
      zipCode: place.zipCode,
      city: place.city,
      country: this.countries.find(c => c.id == place.country.short_name)
        ?.value,
      street2: null,
    };
  }

  createManualEntry() {
    setTimeout(() => {
      document.querySelectorAll('.pac-container').forEach(el => {
        if (!el.querySelector('#manualy')) {
          el.insertAdjacentHTML(
            'beforeend',
            `<div id="manualy" class="pac-item manualy" onmousedown="document.dispatchEvent(new CustomEvent('isGMapsToggle', {detail: this}))">
          <span class="pac-item-query"> Vous ne trouvez pas votre adresse ? </span>
          <span class="pac-item-secondary"> Ajoutez la manuellement </span>
          <span></span>
        </div>`,
          );
        }
      });

      document.querySelectorAll('.pac-container').forEach(el => {
        el.classList.add(`${this.documentMapId}-pac-container`);
      });

      document.querySelectorAll('.pr-map-input').forEach((el, index) => {
        let element = document.querySelectorAll(
          `${this.documentMapId}-pac-container`,
        )[index];
        if (element) el.parentElement.appendChild(element);
      });
    }, 500);
  }

  initMap() {
    this.mapOptions = {
      center: { lat: 46.88272301, lng: 2.03461647 },
      zoom: 13,
      disableDefaultUI: true,
      gestureHandling: 'none',
    };
  }

  @HostListener('document:isGMapsToggle', ['$event'])
  isGMapsToggle(e) {
    this.isGMaps = !this.isGMaps;
  }

  ngAfterViewInit() {
    // Wait for Google Maps API to be loaded
    this.googleMapsLoader.loadScript().subscribe({
      next: () => {
        this.autocomplete = new google.maps.places.Autocomplete(
          this.mapsInput.nativeElement,
        );

        this.autocomplete.addListener('place_changed', () => {
          this.ngZone.run(() => {
            const place = this.autocomplete?.getPlace();
            const result: PlaceSearchResult = {
              address: this.mapsInput.nativeElement.value,
              name: place?.name,
              location: place?.geometry?.location,
              imageUrl: this.getPhotoUrl(place),
              iconUrl: place?.icon,
            };

            this.placeChanged.emit(result);
          });
        });
      },
      error: err => {
        console.error(
          'Failed to load Google Maps in pr-input-maps ngAfterViewInit',
          err,
        );
      },
    });
  }

  getPhotoUrl(
    place: google.maps.places.PlaceResult | undefined,
  ): string | undefined {
    return place?.photos && place?.photos.length > 0
      ? place?.photos[0].getUrl({ maxWidth: 500 })
      : undefined;
  }

  ngOnDestroy() {
    google.maps.event.removeListener(this.autocompleteLstnr);
    google.maps.event.clearInstanceListeners(this.autocomplete);

    const pacContainers = document.querySelectorAll('.pac-container');
    pacContainers.forEach(el => {
      el.remove();
    });
  }

  get control() {
    return this.controlContainer.control;
  }

  findManualAdress() {
    this.fullAddress.setValue(formatAddress(this.control.value), {
      emitEvent: false,
    });

    // Check if Google Maps is loaded
    if (!window.google?.maps) {
      console.warn(
        'Google Maps not loaded yet, skipping manual address search',
      );
      return;
    }

    const displaySuggestions = (
      predictions: google.maps.places.QueryAutocompletePrediction[] | null,
      status: google.maps.places.PlacesServiceStatus,
    ) => {
      if (status != google.maps.places.PlacesServiceStatus.OK || !predictions) {
        throw status;
      }

      let place: google.maps.places.QueryAutocompletePrediction = undefined;

      predictions.some(
        (prediction: google.maps.places.QueryAutocompletePrediction) => {
          //To Refactor
          /* //Use the most matching substrings
        if (prediction.matched_substrings && prediction.matched_substrings.length >= 3) return place = prediction;*/
          // Take the first element
          return (place = prediction);
        },
      );

      if (!place) return;

      this.formattedAddress = place.description;

      geocoder.geocode({ placeId: place.place_id }, results => {
        if (results[0]) {
          this.mapOptions = {
            zoom: 16,
            center: results[0].geometry.location,
          };

          this.marker = {
            position: results[0].geometry.location,
          };

          this.fullAddress.updateValueAndValidity();

          //Remove previous markers
          this.markers.push(this.marker);
          _(this.markers)
            .reverse()
            .map((marker: google.maps.Marker, i) => {
              if (i > 0) {
                marker.setMap(null);
              }
            });
        } else {
          throw 'no result';
        }
      });
    };

    const geocoder = new google.maps.Geocoder();
    const autocompleteService = new google.maps.places.AutocompleteService();

    let addressString = `${this.control.value.street || ''} ${
      this.control.value.city || ''
    } ${this.control.value.zipCode || ''} ${
      countryLabel(this.control.value.country) || ''
    } `;

    if (addressString && addressString.trim().length < 1) return;
    autocompleteService.getQueryPredictions(
      { input: addressString },
      displaySuggestions,
    );
  }

  getAddressValidation(control: AbstractControl): ValidationErrors | null {
    if (
      this.control.get('city').valid &&
      this.control.get('country').valid &&
      this.control.get('zipCode').valid
    )
      return null;
    else return { custom: 'Veuillez saisir une adresse valide' };
  }
  click(event) {
    console.log(event);
  }
}
